import axios from 'axios';
import { getCsrftoken } from '../../helpers/csfrToken';
import { restErrorHandler } from '../../helpers/restErrorHandler';
import { RestApiState } from './Organization';


export interface UserProps {
    id: number;
    first_name: string;
    last_name: string;
    middle_name: string | null;
    organization_id: {
        id: number,
        organization_image:string | null;
        organization_name: string;
        address: string | null;
        phone: number | null;
        email: string | null;
        allow_building_grouping: boolean;
        allow_custom_model_grouping: boolean;
        dealer_organization: null;
    } | null,
    email: string;
    phone: number;
    username: string;
    role: number;
    role_name: string;
    profile_image: string | null;
    date_joined: string;
    invitation_accepted: boolean;
    last_login: string | null
}

export interface CreateUserProps {
    first_name?: string;
    last_name?: string;
    middle_name?: string;
    organization_id?: number,
    email: string;
    phone?: number;
    username: string;
    role: number;
    profile_image?: File;
}


export interface AxiosUserResponseProps {
    count: number;
    next: string | null; // Assuming "next" can be null if there are no more pages
    previous: string | null; // Assuming "previous" can be null if there is no previous page
    results: UserProps[] | undefined;

}


export type Params = {
    search?: string | undefined;
    ordering?: string;
    limit:number;
    offset:number;
    is_manufacture_date: boolean | undefined;
    currentPage: number;
    timeout: NodeJS.Timeout | undefined;
}


function generateQueryString(params: any): string {
    const queryString = Object.entries(params)
        .filter(([key, value]) => value !== undefined) // filter out undefined values
        .map(([key, value]) => {
            if (key === 'offset' && params.limit && params.currentPage) {
                value = (params.currentPage - 1) * params.limit
            }
            if (key === 'currentPage') {
                return null
            }
            return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
        })
        .join('&');

    return queryString ? `?${queryString}` : '';
}


export const AxiosFetchUserList = async (
    params: Params,
    setter: (value: any) => void
) => {
    axios.get<AxiosUserResponseProps>(`${process.env.REACT_APP_DJANGO_SERVER_URL}/api/staff/v1/users/list${generateQueryString(params)}`,
        { withCredentials: true }
    )
    .then(response => {
        if (response.status === 200) {
            setter(response.data)
        }
    })
    .catch(error => {
        restErrorHandler(error.status)
    });
}


export const AxiosDeleteUser = (userId: number, setter: (value: RestApiState) => void) => {
    const headers = {
        headers: {
            'X-CSRFToken': getCsrftoken(),
            'Content-Type': 'multipart/form-data'
        },
        withCredentials: true        
    }
    axios.delete(`${process.env.REACT_APP_DJANGO_SERVER_URL}/api/staff/v1/users/${userId}/details`, headers
    )
    .then(response => {
        if (response.status === 204) {
            setter(RestApiState.Success)
        }
    })
    .catch(error => {
        restErrorHandler(error.status)
    });
}


export interface PatchUser {
    id?: number;
    email: string;
    first_name?: string;
    last_name?: string;
    middle_name?: string;
    phone?: string;
    profile_image?: File;
    organization_id: {
        organization_name: string;
    }
}


export const AxiosFetchSelf = (role:string, setter: (value: PatchUser) => void) => {
    const headers = {
        headers: {
            'X-CSRFToken': getCsrftoken(),
            'Content-Type': 'multipart/form-data'
        },
        withCredentials: true        
    }
    axios.get(`${process.env.REACT_APP_DJANGO_SERVER_URL}/api/${role}/v1/users/self`, headers
    )
    .then(response => {
        if (response.status === 200) {
            setter(response.data)
        }
    })
    .catch(error => {
        restErrorHandler(error.status)
    });
}


export const AxiosPatchSelf = (
    role:string, 
    data: PatchUser, 
    setter: (value: RestApiState) => void,
    setErrors: (value: any) => void
) => {
    const headers = {
        headers: {
            'X-CSRFToken': getCsrftoken(),
            'Content-Type': 'multipart/form-data'
        },
        withCredentials: true        
    }
    axios.patch(`${process.env.REACT_APP_DJANGO_SERVER_URL}/api/${role}/v1/users/self`, data, headers
    )
    .then(response => {
        if (response.status === 200) {
            setter(RestApiState.Success)
        }
    })
    .catch(error => {
        // restErrorHandler(error.status)
        if (error.response.status === 400) {
            setErrors(error.response.data)
            setter(RestApiState.BadRequest)
        }
    });
}


export const AxiosCreateUser = (
    data : CreateUserProps, 
    setStatus: (value: RestApiState) => void,
    setErrors: (value: any) => void
) => {
    const headers = {
        headers: {
            'X-CSRFToken': getCsrftoken(),
            'Content-Type': 'multipart/form-data'
        },
        withCredentials: true        
    }
    axios.post(`${process.env.REACT_APP_DJANGO_SERVER_URL}/api/staff/v1/users/create`, data, headers
    )
    .then(response => {
        if (response.status === 201) {
            setStatus(RestApiState.Success)
        }
    })
    .catch(error => {

        if (error.response.status === 400) {
            setStatus(RestApiState.BadRequest)
            setErrors(error.response.data)
        }
        // restErrorHandler(error.status)
    });
}